import API from "@/utils/api"
import { getResolvedSlug, getRestaurantId, usesHostOnlyPublicPaths } from "@/config/restaurant-id"
import {
  CartTenantIsolationError,
  assertCheckoutCartInvariant,
} from "@/store/cart/cart-tenant-isolation"
import { WEBSITE_ORDER_CHANNEL } from "@/constants/orderChannel"
import type { BillingPreviewUI } from "@/domain/billing/billing-preview.model"
import {
  type BillingPreviewHttpEnvelope,
  mapBillingPreviewResponse,
} from "@/domain/billing/billing-preview.adapter"
import type { Discount } from "@/models/discount"
import type { MenuItemInterface } from "@/models/menu-item"
import type { CartLine } from "@/store/cart/cart.types"
import type {
  BillingPreviewComboMode,
  PublicBillingPreviewRequest,
} from "@/domain/cart/billing-preview.types"
import { mapDiscountsToBillingPreviewPayload } from "@/domain/cart/billing-preview.wire"
import { prepareBillingWireItemsFromCartLines } from "@/services/billing-wire-items"
import { resolveBillingPreviewComboMode } from "@/domain/cart/cart.combo-policy"
import { buildPublicBillingPreviewPath } from "@/services/public-storefront-paths"

export type { BillingPreviewUI } from "@/domain/billing/billing-preview.model"
export type {
  BillingPreviewComboMode,
  BillingPreviewRequest,
  PublicBillingPreviewRequest,
  BillingPreviewV1CartItem,
  BillingItem,
  BillingPreviewDiscountPayload,
} from "@/domain/cart/billing-preview.types"

function newPreviewRequestId(): string {
  try {
    return globalThis.crypto.randomUUID()
  } catch {
    return `preview-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  }
}

function isBillingPreviewDebugLogging(): boolean {
  return (
    process.env.NODE_ENV === "development" ||
    String(process.env.REACT_APP_DEBUG_BILLING_PREVIEW ?? "") === "true"
  )
}

function logBillingPreviewPipelineDebug(
  requestId: string,
  originalLines: CartLine[],
  comboMode: BillingPreviewComboMode,
  wireItemCount: number
): void {
  if (!isBillingPreviewDebugLogging()) return

  const originalIds = originalLines.map((l) => l.client_item_id)

  // eslint-disable-next-line no-console -- intentional billing preview trace
  console.info("[billing-preview] request", {
    requestId,
    comboMode,
    counts: {
      storeLines: originalLines.length,
      wireItems: wireItemCount,
    },
    client_item_ids: {
      store: originalIds,
    },
  })
}

function logBillingPreviewResponseDebug(requestId: string, ui: BillingPreviewUI): void {
  if (!isBillingPreviewDebugLogging()) return
  // eslint-disable-next-line no-console -- paired request/response trace
  console.info("[billing-preview] response", {
    requestId,
    financial: {
      subTotal: ui.subTotal,
      totalDiscount: ui.totalDiscount,
      totalTax: ui.totalTax,
      totalFees: ui.totalFees,
      payableAmount: ui.payableAmount,
    },
  })
}

/** Public billing preview path — mode-aware. */
export function buildStorefrontBillingPreviewPath(slug?: string | null): string {
  return buildPublicBillingPreviewPath(slug)
}

function assertPublicBillingPreviewPath(path: string, slug?: string | null): void {
  if (path !== buildStorefrontBillingPreviewPath(slug)) {
    throw new Error("Invalid billing preview endpoint used")
  }
}

export interface PreviewOrderOptions {
  discounts: { selected?: Discount }
  userName: string
  orderType?: number
  orderChannel?: number
  menuItems?: MenuItemInterface[]
  /** When omitted, resolved from env via `resolveBillingPreviewComboMode()` (API layer only). */
  comboMode?: BillingPreviewComboMode
  /** Backend reference discriminator; defaults to `1`. */
  referenceType?: number
  /**
   * Platform slug from PublicTenantProvider / getResolvedSlug — middleware identity.
   * Required for PLATFORM_SLUG; never derived from RestaurantId.
   */
  slug?: string
}

/**
 * Phase 1B.4-IIIB-1 — public storefront billing preview.
 * POST public/v1/storefront/r/{slug}/billing/preview — no restaurant_id on the wire.
 * Fail closed when slug is missing (no legacy RestaurantId tenancy fallback).
 */
export async function previewOrder(
  cartItems: CartLine[],
  options: PreviewOrderOptions
): Promise<BillingPreviewUI> {
  const requestId = newPreviewRequestId()
  const comboMode = options.comboMode ?? resolveBillingPreviewComboMode()
  const slug = (options.slug ?? getResolvedSlug() ?? "").trim()
  if (!slug && !usesHostOnlyPublicPaths()) {
    throw new Error("Platform slug is required for billing preview")
  }

  assertCheckoutCartInvariant(cartItems, getRestaurantId())

  const items = prepareBillingWireItemsFromCartLines(cartItems, {
    menuItems: options.menuItems,
    comboMode,
  })

  logBillingPreviewPipelineDebug(requestId, cartItems, comboMode, items.length)

  const discounts = mapDiscountsToBillingPreviewPayload(options.discounts ?? { selected: undefined })

  const payload: PublicBillingPreviewRequest = {
    reference_type: options.referenceType ?? 1,
    user_name: options.userName,
    order_channel: options.orderChannel ?? WEBSITE_ORDER_CHANNEL,
    order_type: options.orderType ?? 3,
    discounts,
    items,
  }

  const path = buildStorefrontBillingPreviewPath(slug)

  try {
    assertPublicBillingPreviewPath(path, slug)
    // Pricing callers own UX (inline caption / Continue toast) — never interceptor outage toast.
    const response = await API.post<BillingPreviewHttpEnvelope>(path, payload, {
      skipErrorToast: true,
    })

    if (!response.data?.success || response.data.resultCode !== 200) {
      throw new Error("Failed to calculate billing preview. Please try again.")
    }

    const mapped = mapBillingPreviewResponse(response.data)

    logBillingPreviewResponseDebug(requestId, mapped)

    return mapped
  } catch (error: unknown) {
    if (error instanceof CartTenantIsolationError) {
      throw error
    }
    const e = error as { response?: { data?: { message?: string } }; message?: string }
    const logMessage =
      e?.response?.data?.message || e?.message || "Failed to calculate billing preview"
    if (isBillingPreviewDebugLogging()) {
      // eslint-disable-next-line no-console -- error pairing with requestId
      console.warn("[billing-preview] request failed", { requestId, message: logMessage })
    }
    throw new Error("Failed to calculate billing preview. Please try again.")
  }
}
